import React, { useState } from "react";
import { useLanguage, useT } from "@/lib/LanguageContext";
import useTodaySessions from "../hooks/useTodaySessions";
import useRole from "../hooks/useRole";
import { fmtWeekdayShort } from "../format";
import SectionHeading from "../ui/SectionHeading";
import EmptyState from "../ui/EmptyState";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import SessionCard from "./SessionCard";
import SessionNotePrompt from "./SessionNotePrompt";
import { CRM } from "../../../constants/testIds/crm";

/** The attendees of one slot, by client name (CR-T-88), whatever order the wire lists them in. */
const byName = (a, b) => String(a.client_name || "").localeCompare(String(b.client_name || ""), undefined, { sensitivity: "base" });

/**
 * Zone 1, Today — "the daily tool, mobile-first". One card per booking, in time order,
 * the attendees of a group slot side by side; they scroll horizontally (`snap-x`). The
 * day and its count of sessions (slots) are the SERVER's (`date` in the practice's zone),
 * so "Tue · 4 sessions" is the practice's Tuesday.
 *
 * Fails on its own: a Today read error does not take Nudges or Performance with it.
 */
export default function TodayStrip({ remindersOn = true }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  const { can } = useRole();
  const { data, loading, loadError, reload, complete, noShow, resend, now } = useTodaySessions();
  const [prompt, setPrompt] = useState(null);

  const onComplete = async (a) => {
    const res = await complete(a.booking_id);
    if (res?.prompt_session_note && can("viewSessionNotes")) {
      setPrompt({ clientId: res.client_id || a.client_id, bookingId: a.booking_id, clientName: a.client_name });
    }
  };
  const onNoShow = (a) => noShow(a.booking_id);

  const cards = data?.cards || [];
  // Noon on the server's date, read in the practice's zone: the weekday of `date` itself.
  const weekday = data?.date ? fmtWeekdayShort(`${data.date}T12:00:00Z`, lang, data.timezone) : "";

  return (
    <section aria-labelledby="crm-today-heading" data-testid={CRM.today}>
      <SectionHeading
        id="crm-today-heading"
        title={t("today.title")}
        right={data && (
          <span data-testid={CRM.todayCount} className="text-sm text-eh-muted">
            {t("today.count", { day: weekday, n: cards.length })}
          </span>
        )}
      />
      {loading && !data && (
        <div className="flex gap-3" aria-busy="true">
          {[0, 1].map((i) => <div key={i} className="h-40 w-[208px] shrink-0 animate-pulse rounded-2xl bg-eh-cream" />)}
        </div>
      )}
      {loadError && !data && <CrmLoadFailure error={loadError} onRetry={reload} testId={CRM.todayError} />}
      {data && cards.length === 0 && <EmptyState testId={CRM.todayEmpty} title={t("today.empty")} />}
      {cards.length > 0 && (
        <div className="-mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain px-1 pb-2" data-lenis-prevent>
          {cards.flatMap((card) => [...card.attendees].sort(byName).map((a) => (
            <SessionCard key={a.booking_id} card={card} attendee={a} timezone={data.timezone} now={now}
              onComplete={onComplete} onNoShow={onNoShow} onResend={resend} remindersOn={remindersOn} />
          )))}
        </div>
      )}
      {prompt && <SessionNotePrompt prompt={prompt} onClose={() => setPrompt(null)} />}
    </section>
  );
}
